import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { Table, Button, Spinner, Form } from 'react-bootstrap';
import CollapseHeader from '../../../../core/common/collapse-header';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import Swal from 'sweetalert2';
import { all_routes } from '../../../router/all_routes';
import PendingModule from './PendingModule';
import ApprovedModule from './ApprovedModule';
import FinalApprovedModule from './FinalApprovedModule';
import RejectedModule from './RejectedModule';
import CompletedModule from './CompletedModule'; // Ensure filename case matches exactly
import ViewVisitorRequest from './ViewVisitorRequest';
import EditVisitorRequestModal from './EditVisitorRequestModal';

/** DTOs */
interface VisitorDTO {
  id: number;
  name: string;
  email: string;
  nic: string;
  phone: string;
  visitorCompany: string;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}
interface VehicleDTO {
  id: number;
  vehicleNumber: string;
  parkingSlotId: number | null;
  parkingSlotName?: string | null;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}
interface VisitorRequestDTO {
  id: number;
  locationId: number;
  departmentId: number;
  responsiblePersonEmpNo: string;
  responsiblePersonName?: string;
  responsiblePersonContactNumber?: string;
  visitorPosition: string;
  visitorReason: string;
  parkingRequired: boolean;
  dateTime: string;
  approvedBy: string | null;
  approvedAt: string | null;
  approvalComment: string | null;
  finalApprovedBy: string | null;
  finalApprovedAt: string | null;
  finalApprovalComment: string | null;
  status: 'PENDING' | 'APPROVED' | 'FINAL_APPROVED' | 'REJECTED' | string;
  createdBy?: string;
  visitors: VisitorDTO[];
  vehicles: VehicleDTO[];
}

interface GetVisitorsResponse {
  status: 'SUCCESS' | 'S1000' | string;
  statusDescription: string;
  data: VisitorRequestDTO[];
  total: number;
}

interface VisitorCounts {
  pending: number;
  approved: number;
  finalApproved: number;
  rejected: number;
  completed: number;
}

interface UserLite {
  empNumber: string;
  userType: string;
}

/** Permissions */
const statusPermissions: { [key: string]: string } = {
  PENDING: 'ROLE_PENDING_VEHICLE_BOOKING',
  APPROVED: 'ROLE_APPROVE_VISITOR_REQUEST',
  FINAL_APPROVED: 'ROLE_FINAL_APPROVE_VISITOR_REQUEST',
  REJECTED: 'ROLE_FINAL_APPROVE_VISITOR_REQUEST',
  COMPLETED: 'ROLE_FINAL_APPROVE_VISITOR_REQUEST',
};

const statusLabels: { [key: string]: string } = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  FINAL_APPROVED: 'Final Approved',
  REJECTED: 'Rejected',
  COMPLETED: 'Completed',
};

type CardStatus = 'PENDING' | 'APPROVED' | 'FINAL_APPROVED' | 'REJECTED' | 'COMPLETED';

/** Badge class for the table Status column */
const statusBadgeClass: Record<string, string> = {
  PENDING: 'badge bg-warning',
  APPROVED: 'badge bg-secondary',
  FINAL_APPROVED: 'badge bg-info',
  REJECTED: 'badge bg-danger',
  COMPLETED: 'badge bg-success',
};

/** Card styles */
const statusCardStyles: {
  [key: string]: { bgColor: string; iconColor: string; icon: string; className: string };
} = {
  PENDING: { bgColor: '#fef3c7', iconColor: '#f59e0b', icon: 'fas fa-clock', className: 'bg-amber-100' },
  APPROVED: { bgColor: '#d1fae5', iconColor: '#10b981', icon: 'fas fa-check-circle', className: 'bg-green-100' },
  FINAL_APPROVED: { bgColor: '#e0f2fe', iconColor: '#3b82f6', icon: 'fas fa-check-double', className: 'bg-blue-100' },
  REJECTED: { bgColor: '#fee2e2', iconColor: '#ef4444', icon: 'fas fa-times-circle', className: 'bg-red-100' },
  COMPLETED: { bgColor: '#dbeafe', iconColor: '#6366f1', icon: 'fas fa-flag-checkered', className: 'bg-indigo-100' },
};

/** Nav items */
const navItems = [
  { label: 'Visitor Dashboard', icon: 'ti ti-users', link: all_routes.Visitor, permission: 'ROLE_GET_VISITOR_REQUEST' },
  { label: 'Add Visitor', icon: 'ti ti-plus', link: all_routes.visitorRequest, permission: 'ROLE_ADD_VISITOR' },
];

const Visitor: React.FC = () => {
  const locationHook = useLocation();
  const [currentUser, setCurrentUser] = useState<UserLite | null>(null);
  const [counts, setCounts] = useState<VisitorCounts>({ pending: 0, approved: 0, finalApproved: 0, rejected: 0, completed: 0 });
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsError, setCountsError] = useState<string | null>(null);

  const [myVisitorRequests, setMyVisitorRequests] = useState<VisitorRequestDTO[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [errorRequests, setErrorRequests] = useState<string | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateFilter, setDateFilter] = useState('');
  const [rowLoadingId, setRowLoadingId] = useState<number | null>(null);

  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showApprovedModal, setShowApprovedModal] = useState(false);
  const [showFinalApprovedModal, setShowFinalApprovedModal] = useState(false);
  const [showRejectedModal, setShowRejectedModal] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVisitorRequestId, setSelectedVisitorRequestId] = useState<number | null>(null);
  const [selectedVisitorRequest, setSelectedVisitorRequest] = useState<VisitorRequestDTO | null>(null);

  const isOk = (res: AxiosResponse<GetVisitorsResponse>) =>
    res.status === 200 && (res.data.status === 'SUCCESS' || res.data.status === 'S1000');

  useEffect(() => {
    const storedUsername = localStorage.getItem('username') || '';
    const storedGroup =
      localStorage.getItem('group') ||
      localStorage.getItem('role') ||
      localStorage.getItem('userType') ||
      'USER';
    setCurrentUser({ empNumber: storedUsername, userType: storedGroup });
  }, []);

  /** Helper: safely get count from list-style ApiResponse<T[]> endpoints */
  const getListCount = (res: any): number => {
    try {
      const arr = res?.data?.data;
      return Array.isArray(arr) ? arr.length : 0;
    } catch {
      return 0;
    }
  };

  /** Helper: fetch all pages for a given STATUS and merge them */
  const fetchAllVisitorsByStatus = useCallback(
    async (statusValue: string): Promise<VisitorRequestDTO[]> => {
      // First page
      const first = await apiDefinitions.getVisitors('1', {
        filterType: 'STATUS', // normalized to uppercase
        filterValue: statusValue,
        date: '',
      });
      if (!isOk(first)) return [];

      const data1 = Array.isArray(first.data?.data) ? (first.data.data as VisitorRequestDTO[]) : [];
      const totalRecords = Number(first.data?.total ?? data1.length ?? 0);
      const pageSize = data1.length > 0 ? data1.length : 15;

      if (totalRecords <= pageSize) return data1;

      const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
      const pagePromises: Promise<AxiosResponse<GetVisitorsResponse>>[] = [];
      for (let p = 1; p <= totalPages; p++) {
        pagePromises.push(
          apiDefinitions.getVisitors(String(p), {
            filterType: 'STATUS',
            filterValue: statusValue,
            date: '',
          })
        );
      }
      const rest = await Promise.allSettled(pagePromises);

      const others: VisitorRequestDTO[] = [];
      for (const r of rest) {
        if (r.status === 'fulfilled' && isOk(r.value)) {
          const rows = Array.isArray(r.value.data?.data) ? (r.value.data.data as VisitorRequestDTO[]) : [];
          others.push(...rows);
        }
      }
      return [...data1, ...others];
    },
    [] // uses apiDefinitions + isOk which are stable here
  );

  /** Counts loader */
  const fetchCounts = useCallback(async () => {
    setCountsLoading(true);
    setCountsError(null);
    try {
      const calls: Promise<{ status: CardStatus; total: number }>[] = [];

      // PENDING
      if (checkPermission('ROLE_PENDING_VEHICLE_BOOKING')) {
        calls.push(
          (async () => {
            try {
              const res = await apiDefinitions.getPendingApprovalVisitors('1');
              if (res.status === 200 && (res.data.status === 'SUCCESS' || res.data.status === 'S1000')) {
                return { status: 'PENDING' as CardStatus, total: res.data.total || 0 };
              }
              return { status: 'PENDING' as CardStatus, total: 0 };
            } catch {
              return { status: 'PENDING' as CardStatus, total: 0 };
            }
          })()
        );
      } else {
        calls.push(Promise.resolve({ status: 'PENDING' as CardStatus, total: 0 }));
      }

      // APPROVED
      if (checkPermission('ROLE_APPROVE_VISITOR_REQUEST')) {
        calls.push(
          (async () => {
            try {
              const res = await apiDefinitions.getApprovedVisitors();
              return { status: 'APPROVED' as CardStatus, total: getListCount(res) };
            } catch {
              return { status: 'APPROVED' as CardStatus, total: 0 };
            }
          })()
        );
      } else {
        calls.push(Promise.resolve({ status: 'APPROVED' as CardStatus, total: 0 }));
      }

      // FINAL APPROVED (fallback to generic filtered list)
      if (checkPermission('ROLE_FINAL_APPROVE_VISITOR_REQUEST')) {
        calls.push(
          (async () => {
            try {
              const res = await apiDefinitions.getVisitors('1', {
                filterType: 'STATUS',
                filterValue: 'FINAL_APPROVED',
                date: '',
              });
              if (isOk(res)) return { status: 'FINAL_APPROVED' as CardStatus, total: res.data.total || 0 };
              return { status: 'FINAL_APPROVED' as CardStatus, total: 0 };
            } catch {
              return { status: 'FINAL_APPROVED' as CardStatus, total: 0 };
            }
          })()
        );
      } else {
        calls.push(Promise.resolve({ status: 'FINAL_APPROVED' as CardStatus, total: 0 }));
      }

      // REJECTED (separate API)
      if (checkPermission('ROLE_FINAL_APPROVE_VISITOR_REQUEST')) {
        calls.push(
          (async () => {
            try {
              const resRejected = await apiDefinitions.getRejectedVisitors();
              const totalRejected = getListCount(resRejected);
              return { status: 'REJECTED' as CardStatus, total: totalRejected };
            } catch {
              return { status: 'REJECTED' as CardStatus, total: 0 };
            }
          })()
        );
      } else {
        calls.push(Promise.resolve({ status: 'REJECTED' as CardStatus, total: 0 }));
      }

      // COMPLETED: FINAL_APPROVED where dateTime < now (across ALL pages)
      if (checkPermission(statusPermissions['COMPLETED'])) {
        calls.push(
          (async () => {
            try {
              const allFinalApproved = await fetchAllVisitorsByStatus('FINAL_APPROVED');
              const now = new Date();
              const completedCount = allFinalApproved.filter((r: VisitorRequestDTO) => {
                const dt = r?.dateTime ? new Date(r.dateTime) : null;
                return dt instanceof Date && !isNaN(dt.getTime()) && dt < now;
              }).length;
              return { status: 'COMPLETED' as CardStatus, total: completedCount };
            } catch {
              return { status: 'COMPLETED' as CardStatus, total: 0 };
            }
          })()
        );
      } else {
        calls.push(Promise.resolve({ status: 'COMPLETED' as CardStatus, total: 0 }));
      }

      const results = await Promise.all(calls);
      const nextCounts: VisitorCounts = { pending: 0, approved: 0, finalApproved: 0, rejected: 0, completed: 0 };
      results.forEach(({ status, total }) => {
        if (status === 'PENDING') nextCounts.pending = total;
        if (status === 'APPROVED') nextCounts.approved = total;
        if (status === 'FINAL_APPROVED') nextCounts.finalApproved = total;
        if (status === 'REJECTED') nextCounts.rejected = total;
        if (status === 'COMPLETED') nextCounts.completed = total;
      });
      setCounts(nextCounts);
    } catch (e: any) {
      setCountsError('Failed to fetch visitor counts. Please try again.');
    } finally {
      setCountsLoading(false);
    }
  }, [fetchAllVisitorsByStatus]);

  const fetchMyVisitorRequests = useCallback(
    async (page: number) => {
      if (!checkPermission('ROLE_GET_VISITOR_REQUEST')) {
        setErrorRequests('You do not have permission to view visitor requests.');
        return;
      }
      setLoadingRequests(true);
      setErrorRequests(null);
      try {
        const payload = { filterType: '', filterValue: '', date: dateFilter || '' };
        const res: AxiosResponse<GetVisitorsResponse> = await apiDefinitions.getUserCreatedVisitors(page.toString(), payload);
        if (isOk(res)) {
          const rows = res.data.data ?? [];
          setMyVisitorRequests(rows);
          setTotal(res.data.total ?? 0);
          setErrorRequests(null);
        } else {
          setErrorRequests(res.data.statusDescription || 'Failed to fetch visitor requests');
        }
      } catch (err: any) {
        const msg = err.response?.data?.error || err.response?.data?.statusDescription || err.message;
        setErrorRequests(`Failed to fetch visitor requests: ${msg}`);
      } finally {
        setLoadingRequests(false);
      }
    },
    [dateFilter]
  );

  const canEditDelete = (status: string) => {
    const role = currentUser?.userType || 'USER';
    if (role === 'SERVICE_MANAGER') return true;
    if (role === 'DEPARTMENT_HEAD') return status === 'PENDING' || status === 'APPROVED';
    return status === 'PENDING';
  };

  const handleCardClick = (status: CardStatus) => {
    if (status === 'PENDING') setShowPendingModal(true);
    if (status === 'APPROVED') setShowApprovedModal(true);
    if (status === 'FINAL_APPROVED') setShowFinalApprovedModal(true);
    if (status === 'REJECTED') setShowRejectedModal(true);
    if (status === 'COMPLETED') setShowCompletedModal(true);
  };

  const handleViewClick = (visitorRequestId: number) => {
    setSelectedVisitorRequestId(visitorRequestId);
    setShowViewModal(true);
  };

  const handleEditClick = async (id: number) => {
    try {
      setRowLoadingId(id);
      const res = await apiDefinitions.getVisitorById(id);
      if (res.status === 200 && (res.data.status === 'SUCCESS' || res.data.status === 'S1000') && res.data.data) {
        setSelectedVisitorRequest(res.data.data);
        setShowEditModal(true);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed to open editor',
          text: res.data?.statusDescription || 'Unable to load the visitor request.',
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to open editor',
        text: err.response?.data?.statusDescription || 'Unable to load the visitor request.',
      });
    } finally {
      setRowLoadingId(null);
    }
  };

  const handleDeleteRequest = async (requestId: number) => {
    if (!checkPermission('ROLE_UPDATE_VISITOR_REQUEST')) {
      Swal.fire({ icon: 'error', title: 'Permission Denied', text: 'You do not have permission to delete visitor requests.' });
      return;
    }
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this visitor request?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      setRowLoadingId(requestId);
      const res = await apiDefinitions.deleteVisitor(requestId);
      if (res.status === 200) {
        Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Visitor request has been deleted.' });
        setMyVisitorRequests((prev) => prev.filter((r) => r.id !== requestId));
        fetchCounts();
        fetchMyVisitorRequests(pageNo);
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: res.data?.statusDescription || 'Failed to delete visitor request' });
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.statusDescription || 'Failed to delete visitor request',
      });
    } finally {
      setRowLoadingId(null);
    }
  };

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.max(1, Math.ceil(total / 15));
    if (newPage > 0 && newPage <= totalPages) {
      setPageNo(newPage);
      fetchMyVisitorRequests(newPage);
    }
  };

  const handleDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFilter(e.target.value);
    setPageNo(1);
  };

  const handleModalActionComplete = useCallback(() => {
    fetchCounts();
    fetchMyVisitorRequests(pageNo);
  }, [fetchCounts, fetchMyVisitorRequests, pageNo]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchMyVisitorRequests(pageNo);
  }, [fetchMyVisitorRequests, pageNo, dateFilter]);

  const formatDate = (iso?: string | null) => {
    if (!iso) return 'N/A';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
  };

  const FallbackSpinner = () => (
    <div className="spinner-border spinner-border-sm" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  );

  return (
    <div className="page-wrapper">
      <style>{`
        .stat-card { transition: all 0.3s ease; }
        .stat-card:hover { transform: translateY(-5px); box-shadow: 0 10px 20px rgba(0,0,0,0.1); }
        .icon-container { height: 60px; width: 60px; display: flex; align-items: center; justify-content: center; border-radius: 12px; }
        .table-responsive { margin-top: 1.5rem; }
      `}</style>

      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Visitor Management/Visitor Dashboard</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>


            <div className="row mt-4" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
              {countsLoading && (
                <div className="text-center mb-4">
                  {Spinner ? <Spinner animation="border" size="sm" /> : <FallbackSpinner />} Loading counts...
                </div>
              )}
              {countsError && (
                <div className="alert alert-danger mb-4" role="alert">
                  {countsError}
                </div>
              )}
              {(['PENDING', 'APPROVED', 'FINAL_APPROVED', 'REJECTED', 'COMPLETED'] as const).map((status) =>
                checkPermission(statusPermissions[status]) ? (
                  <div className="col-xl-3 col-sm-6 col-12 mb-4" style={{ padding: '0 15px' }} key={status}>
                    <div
                      className="stat-card bg-white rounded-xl shadow-md overflow-hidden"
                      style={{ borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', cursor: 'pointer' }}
                      onClick={() => handleCardClick(status)}
                    >
                      <div className="p-6" style={{ padding: '1.5rem' }}>
                        <div
                          className="flex items-center justify-between mb-4"
                          style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                          <div
                            className={`icon-container ${statusCardStyles[status]?.className || 'bg-gray-100'}`}
                            style={{ backgroundColor: statusCardStyles[status]?.bgColor || '#f3f4f6' }}
                          >
                            <i
                              className={statusCardStyles[status]?.icon || 'fas fa-info-circle'}
                              style={{ fontSize: '1.5rem', color: statusCardStyles[status]?.iconColor || '#6b7280' }}
                            />
                          </div>
                          <span style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}>
                            {status === 'PENDING'
                              ? counts.pending
                              : status === 'APPROVED'
                              ? counts.approved
                              : status === 'FINAL_APPROVED'
                              ? counts.finalApproved
                              : status === 'REJECTED'
                              ? counts.rejected
                              : counts.completed}
                          </span>
                        </div>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: 0 }}>
                          {statusLabels[status] || status}
                        </h3>
                      </div>
                    </div>
                  </div>
                ) : null
              )}
            </div>

            <div className="row mt-4">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title">My Visitor Requests</h5>
                  </div>
                  <div className="card-body">
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <Form.Group>
                          <Form.Label>Filter by Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={dateFilter}
                            onChange={handleDateFilterChange}
                            disabled={loadingRequests}
                          />
                        </Form.Group>
                      </div>
                    </div>

                    {loadingRequests && (
                      <div className="d-flex align-items-center gap-2">
                        {Spinner ? <Spinner animation="border" size="sm" /> : <FallbackSpinner />}
                        <span>Loading...</span>
                      </div>
                    )}

                    {!loadingRequests && errorRequests && (
                      <div className="alert alert-danger" role="alert">
                        {errorRequests}
                      </div>
                    )}

                    {!loadingRequests && !errorRequests && myVisitorRequests.length === 0 && (
                      <div className="alert alert-info" role="alert">
                        No visitor requests found.
                      </div>
                    )}

                    {!loadingRequests && !errorRequests && myVisitorRequests.length > 0 && (
                      <div className="table-responsive">
                        <Table className="table text-nowrap">
                          <thead className="table-primary">
                            <tr>
                              <th>ID</th>
                              <th>Visitor Position</th>
                              <th>Visitor Reason</th>
                              <th>Date & Time</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {myVisitorRequests.map((request) => {
                              const busy = rowLoadingId === request.id;
                              return (
                                <tr key={request.id}>
                                  <td>{request.id}</td>
                                  <td>{request.visitorPosition || 'N/A'}</td>
                                  <td>{request.visitorReason || 'N/A'}</td>
                                  <td>{formatDate(request.dateTime)}</td>
                                  <td>
                                    <span className={statusBadgeClass[request.status] || 'badge bg-secondary'}>
                                      {statusLabels[request.status] || request.status}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="hstack gap-2">
                                      <Button
                                        variant="icon btn-secondary btn-sm"
                                        title="View Request"
                                        onClick={() => handleViewClick(request.id)}
                                        disabled={busy}
                                      >
                                        {busy ? <i className="ti ti-loader" /> : <i className="ti ti-eye" />}
                                      </Button>
                                      {canEditDelete(request.status) && (
                                        <>
                                          <Button
                                            variant="icon btn-info btn-sm"
                                            title="Edit Request"
                                            onClick={() => handleEditClick(request.id)}
                                            disabled={busy}
                                          >
                                            {busy ? <i className="ti ti-loader" /> : <i className="ti ti-edit" />}
                                          </Button>
                                          <Button
                                            variant="icon btn-danger btn-sm"
                                            title="Delete Request"
                                            onClick={() => handleDeleteRequest(request.id)}
                                            disabled={busy}
                                          >
                                            {busy ? <i className="ti ti-loader" /> : <i className="ti ti-trash" />}
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>

                        <div className="d-flex justify-content-between mt-3">
                          <Button
                            variant="outline-primary"
                            disabled={pageNo === 1 || loadingRequests}
                            onClick={() => handlePageChange(pageNo - 1)}
                          >
                            Previous
                          </Button>
                          <span>
                            Page {pageNo} of {Math.max(1, Math.ceil(total / 15))}
                          </span>
                          <Button
                            variant="outline-primary"
                            disabled={pageNo >= Math.max(1, Math.ceil(total / 15)) || loadingRequests}
                            onClick={() => handlePageChange(pageNo + 1)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <PendingModule
              show={showPendingModal}
              onHide={() => {
                setShowPendingModal(false);
                handleModalActionComplete();
              }}
            />

            <ApprovedModule
              show={showApprovedModal}
              onHide={() => {
                setShowApprovedModal(false);
                handleModalActionComplete();
              }}
            />
            <FinalApprovedModule
              show={showFinalApprovedModal}
              onHide={() => {
                setShowFinalApprovedModal(false);
                handleModalActionComplete();
              }}
            />
            <RejectedModule
              show={showRejectedModal}
              onHide={() => {
                setShowRejectedModal(false);
                handleModalActionComplete();
              }}
            />
            <CompletedModule
              show={showCompletedModal}
              onHide={() => {
                setShowCompletedModal(false);
                handleModalActionComplete();
              }}
            />
            {showViewModal && selectedVisitorRequestId !== null && (
              <ViewVisitorRequest
                show={showViewModal}
                onHide={() => setShowViewModal(false)}
                visitorRequestId={selectedVisitorRequestId}
              />
            )}
            {showEditModal && selectedVisitorRequest !== null && (
              <EditVisitorRequestModal
                show={showEditModal}
                onHide={() => {
                  setShowEditModal(false);
                  setSelectedVisitorRequest(null);
                }}
                data={selectedVisitorRequest}
                onUpdated={handleModalActionComplete}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Visitor;
